import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["map", "filterCountry", "filterCity", "searchInput", "clearFilters", "activeFilters"]
  static values = {
    festivals: Array,
    centerLat: Number,
    centerLng: Number,
    zoom: Number
  }

  connect() {
    if (typeof mapboxgl === 'undefined') {
      console.error("Mapbox GL JS is not loaded")
      return
    }

    // Check if we have the map target
    if (!this.hasMapTarget) {
      console.error("Map target not found")
      return
    }

    // Initialize state
    this.currentFilters = {
      country: 'all',
      city: 'all'
    }

    this.hoveredMarkerId = null

    // Initialize map
    this.initializeMap()
  }

  disconnect() {
    // Clean up map
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  initializeMap() {
    const token = this.authToken
    if (!token) {
      console.error("Mapbox token not found")
      this.showMapError("Token Mapbox manquant")
      return
    }

    mapboxgl.accessToken = token

    try {
      // Wait for the map container to be properly sized
      setTimeout(() => {
        this.map = new mapboxgl.Map({
          container: this.mapTarget,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [this.centerLngValue || 2.3522, this.centerLatValue || 48.8566],
          zoom: this.zoomValue || 5,
          projection: 'mercator',
          pitch: 0,
          bearing: 0
        })

        this.map.addControl(new mapboxgl.NavigationControl())

        this.map.on('load', () => {
          this.addFestivalMarkersWithClustering()
          this.setupMapInteractions()
        })

        this.map.on('error', (e) => {
          console.error("Map error:", e)
          this.showMapError("Erreur lors du chargement de la carte")
        })

        // Handle map resize
        this.map.on('idle', () => {
          this.map.resize()
        })
      }, 100)
    } catch (error) {
      console.error("Failed to initialize map:", error)
      this.showMapError("Impossible d'initialiser la carte")
    }
  }

  highlightMapMarker(festivalId) {
    if (!this.map || !this.map.getSource('festivals')) return

    const festival = this.festivalsValue.find(f => f.id == festivalId)
    if (!festival || !festival.latitude || !festival.longitude) {
      return
    }

    // Remove any previous highlight
    if (this.hoveredMarkerId && this.hoveredMarkerId !== festivalId) {
      this.map.setFeatureState(
        { source: 'festivals', id: parseInt(this.hoveredMarkerId) },
        { hover: false }
      )
    }

    // Set feature state for highlight
    this.map.setFeatureState(
      { source: 'festivals', id: parseInt(festivalId) },
      { hover: true }
    )

    this.hoveredMarkerId = festivalId
  }

  unhighlightMapMarker(festivalId) {
    if (!this.map || !this.map.getSource('festivals')) return

    this.map.setFeatureState(
      { source: 'festivals', id: parseInt(festivalId) },
      { hover: false }
    )

    if (this.hoveredMarkerId == festivalId) {
      this.hoveredMarkerId = null
    }
  }

  centerMapOnFestival(festivalId) {
    const festival = this.festivalsValue.find(f => f.id == festivalId)
    if (!festival || !festival.latitude || !festival.longitude) return

    this.map.flyTo({
      center: [festival.longitude, festival.latitude],
      zoom: 14,
      duration: 1000
    })
  }

  setupMapInteractions() {
    // Hover effect on clusters
    this.map.on('mouseenter', 'clusters', () => {
      this.map.getCanvas().style.cursor = 'pointer'
    })

    this.map.on('mouseleave', 'clusters', () => {
      this.map.getCanvas().style.cursor = ''
    })

    // Hover effect on individual points
    this.map.on('mouseenter', 'unclustered-point', (e) => {
      this.map.getCanvas().style.cursor = 'pointer'

      const properties = e.features[0].properties
      const festivalId = properties.id

      this.highlightMapMarker(festivalId)
    })

    this.map.on('mouseleave', 'unclustered-point', () => {
      this.map.getCanvas().style.cursor = ''

      if (this.hoveredMarkerId) {
        this.unhighlightMapMarker(this.hoveredMarkerId)
      }
    })

    // Click on marker
    this.map.on('click', 'unclustered-point', (e) => {
      const properties = e.features[0].properties

      const festival = {
        id: properties.id,
        name: properties.name,
        city: properties.city,
        country: properties.country,
        genres: properties.genres,
        indoor_outdoor: properties.indoor_outdoor,
        show_url: properties.show_url,
        edit_url: properties.edit_url,
        latitude: e.features[0].geometry.coordinates[1],
        longitude: e.features[0].geometry.coordinates[0]
      }

      this.showFestivalPopup(festival)
    })

    // Click on cluster
    this.map.on('click', 'clusters', (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ['clusters']
      })

      const clusterId = features[0].properties.cluster_id
      this.map.getSource('festivals').getClusterExpansionZoom(
        clusterId,
        (err, zoom) => {
          if (err) return

          this.map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom
          })
        }
      )
    })
  }

  addFestivalMarkersWithClustering() {
    // Filter festivals with coordinates
    const festivalsWithCoords = this.festivalsValue.filter(f => f.latitude && f.longitude)

    // Apply jitter to prevent stacked markers at same location
    const festivalsWithJitter = this.applyJitterToOverlappingFestivals(festivalsWithCoords)

    // Convert to GeoJSON
    const geojson = {
      type: 'FeatureCollection',
      features: festivalsWithJitter.map((festival) => ({
        type: 'Feature',
        id: parseInt(festival.id),
        geometry: {
          type: 'Point',
          coordinates: [festival.longitude, festival.latitude]
        },
        properties: {
          id: festival.id,
          name: festival.name,
          city: festival.city,
          country: festival.country,
          genres: festival.genres,
          indoor_outdoor: festival.indoor_outdoor,
          show_url: festival.show_url,
          edit_url: festival.edit_url
        }
      }))
    }

    // Add source
    this.map.addSource('festivals', {
      type: 'geojson',
      data: geojson,
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50
    })

    // Add cluster layer
    this.map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'festivals',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#8b5cf6',  // Purple for festivals
          15,
          '#f59e0b',  // Orange
          30,
          '#ef4444'   // Red
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          20,
          15,
          30,
          30,
          40
        ]
      }
    })

    // Add cluster count layer
    this.map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'festivals',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      }
    })

    // Add individual festival points
    this.map.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'festivals',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          '#f59e0b',  // Orange when hovered
          '#8b5cf6'   // Purple for festivals
        ],
        'circle-radius': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          10,
          7
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff'
      }
    })

    // Fit map to festival bounds
    if (festivalsWithCoords.length > 0) {
      const bounds = new mapboxgl.LngLatBounds()
      festivalsWithCoords.forEach(festival => {
        bounds.extend([festival.longitude, festival.latitude])
      })
      this.map.fitBounds(bounds, { padding: 50 })
    }
  }

  // Filter methods
  filterFestivals() {
    const searchTerm = this.hasSearchInputTarget ? this.searchInputTarget.value.toLowerCase() : ''
    const country = this.hasFilterCountryTarget ? this.filterCountryTarget.value : 'all'
    const city = this.hasFilterCityTarget ? this.filterCityTarget.value : 'all'

    // Filter festivals (only those with coordinates can be shown on map)
    const filteredFestivals = this.festivalsValue.filter(festival => {
      // Only show geolocated festivals on map
      if (!festival.latitude || !festival.longitude) {
        return false
      }

      // Search filter
      if (searchTerm &&
          !festival.name?.toLowerCase().includes(searchTerm) &&
          !festival.city?.toLowerCase().includes(searchTerm) &&
          !festival.country?.toLowerCase().includes(searchTerm) &&
          !festival.genres?.toLowerCase().includes(searchTerm)) {
        return false
      }

      // Country filter
      if (country !== 'all' && festival.country !== country) {
        return false
      }

      // City filter
      if (city !== 'all' && festival.city !== city) {
        return false
      }

      return true
    })

    // Update map markers
    this.updateMapMarkers(filteredFestivals)

    // Update stats display
    this.updateStatsDisplay(filteredFestivals)

    // Show/hide clear button
    if (searchTerm || country !== 'all' || city !== 'all') {
      this.clearFiltersTarget?.classList.remove('hidden')
    } else {
      this.clearFiltersTarget?.classList.add('hidden')
    }
  }

  clearFilters() {
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ''
    if (this.hasFilterCountryTarget) this.filterCountryTarget.value = 'all'
    if (this.hasFilterCityTarget) this.filterCityTarget.value = 'all'

    this.filterFestivals()
  }

  updateStatsDisplay(filteredFestivals) {
    // Find stats elements in the DOM
    const statsContainer = this.element.querySelector('.absolute.top-24.left-4')
    if (!statsContainer) return

    const withCoordsCount = filteredFestivals.filter(f => f.latitude && f.longitude).length

    // Update primary count badge
    const primaryBadge = statsContainer.querySelector('.text-teal-600')?.closest('.flex')
    if (primaryBadge) {
      const countSpan = primaryBadge.querySelector('.font-bold')
      if (countSpan) {
        countSpan.textContent = withCoordsCount
      }
    }
  }

  updateMapMarkers(festivals) {
    if (!this.map || !this.map.getSource('festivals')) return

    const festivalsWithCoords = festivals.filter(f => f.latitude && f.longitude)
    const festivalsWithJitter = this.applyJitterToOverlappingFestivals(festivalsWithCoords)

    const geojson = {
      type: 'FeatureCollection',
      features: festivalsWithJitter.map(festival => ({
        type: 'Feature',
        id: parseInt(festival.id),
        geometry: {
          type: 'Point',
          coordinates: [festival.longitude, festival.latitude]
        },
        properties: {
          id: festival.id,
          name: festival.name,
          city: festival.city,
          country: festival.country,
          genres: festival.genres,
          indoor_outdoor: festival.indoor_outdoor,
          show_url: festival.show_url,
          edit_url: festival.edit_url
        }
      }))
    }

    this.map.getSource('festivals').setData(geojson)
  }

  showFestivalPopup(festival) {
    const popupContent = `
      <div class="min-w-[280px]">
        <!-- Header with festival name -->
        <div class="px-4 pt-4 pb-3 border-b border-gray-100">
          <h3 class="text-lg font-semibold text-gray-900 mb-1">${festival.name}</h3>
          <div class="flex items-center gap-1.5 text-sm text-gray-600">
            <svg class="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/>
            </svg>
            <span>${festival.city}, ${festival.country}</span>
          </div>
          ${festival.genres ? `
            <div class="mt-2">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                ${festival.genres}
              </span>
            </div>
          ` : ''}
          ${festival.indoor_outdoor ? `
            <div class="mt-1">
              <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                ${festival.indoor_outdoor}
              </span>
            </div>
          ` : ''}
        </div>

        <!-- Actions -->
        <div class="px-4 py-3 bg-gray-50 flex gap-2">
          <a href="${festival.show_url}"
             class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors shadow-sm">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
            </svg>
            <span>Voir</span>
          </a>
          <a href="${festival.edit_url}"
             data-turbo-frame="modal"
             class="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
            </svg>
            <span>Modifier</span>
          </a>
        </div>
      </div>
    `

    new mapboxgl.Popup({
      maxWidth: '320px',
      className: 'festival-popup'
    })
      .setLngLat([festival.longitude, festival.latitude])
      .setHTML(popupContent)
      .addTo(this.map)
  }

  showMapError(message) {
    if (this.hasMapTarget) {
      this.mapTarget.innerHTML = `
        <div class="flex items-center justify-center h-full bg-gray-100">
          <div class="text-center p-8">
            <i class="fas fa-map-marked-alt text-6xl text-gray-400 mb-4"></i>
            <p class="text-gray-600 text-lg">${message}</p>
            <p class="text-gray-500 text-sm mt-2">Vérifiez la configuration Mapbox</p>
          </div>
        </div>
      `
    }
  }

  get authToken() {
    const meta = document.head.querySelector('meta[name="mapbox_token"]')
    if (!meta) {
      console.error("Token Mapbox non trouvé dans les meta tags")
      return null
    }
    return meta.content
  }

  applyJitterToOverlappingFestivals(festivals) {
    // Group festivals by exact coordinates
    const coordsMap = new Map()

    festivals.forEach(festival => {
      // Ensure latitude and longitude are numbers
      const lat = parseFloat(festival.latitude)
      const lng = parseFloat(festival.longitude)
      if (isNaN(lat) || isNaN(lng)) return

      const key = `${lat.toFixed(4)},${lng.toFixed(4)}`
      if (!coordsMap.has(key)) {
        coordsMap.set(key, [])
      }
      coordsMap.get(key).push(festival)
    })

    // Apply jitter to festivals at same location
    const jitteredFestivals = []

    coordsMap.forEach((festivalsAtLocation) => {
      if (festivalsAtLocation.length === 1) {
        // Ensure coordinates are numbers even for single festivals
        const festival = festivalsAtLocation[0]
        jitteredFestivals.push({
          ...festival,
          latitude: parseFloat(festival.latitude),
          longitude: parseFloat(festival.longitude)
        })
      } else {
        festivalsAtLocation.forEach((festival, index) => {
          const lat = parseFloat(festival.latitude)
          const lng = parseFloat(festival.longitude)

          // Jitter amount: ~220 meters (0.002 degrees)
          const jitterAmount = 0.002
          const angle = (2 * Math.PI * index) / festivalsAtLocation.length
          const radius = jitterAmount * (0.7 + Math.random() * 0.3)

          jitteredFestivals.push({
            ...festival,
            latitude: lat + (radius * Math.sin(angle)),
            longitude: lng + (radius * Math.cos(angle))
          })
        })
      }
    })

    return jitteredFestivals
  }
}
